// Updated FeedbackModal.jsx - Firebase Only Solution
import React, { useState } from 'react';
import { db } from '../firebase'; // Your existing Firebase config
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import './FeedbackModal.css';

const FeedbackModal = ({ isOpen, onClose, user }) => {
  const [feedback, setFeedback] = useState({
    type: 'suggestion',
    message: '',
    email: '',
    rating: 5
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Save directly to Firestore
      await addDoc(collection(db, 'feedback'), {
        type: feedback.type,
        message: feedback.message,
        rating: feedback.rating,
        userEmail: feedback.email || user?.email || 'anonymous',
        userId: user?.uid || null,
        userName: user?.displayName || 'Anonymous',
        timestamp: serverTimestamp(),
        status: 'new',
        // Add some context that might be helpful
        userAgent: navigator.userAgent,
        url: window.location.href
      });

      setIsSubmitted(true);
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        onClose();
        setIsSubmitted(false);
        setFeedback({
          type: 'suggestion',
          message: '',
          email: '',
          rating: 5
        });
      }, 3000);

    } catch (error) {
      console.error('Error sending feedback:', error);
      setError('Failed to send feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (isSubmitted) {
    return (
      <div className="feedback-overlay">
        <div className="feedback-modal">
          <div className="feedback-success">
            <div className="success-icon">✓</div>
            <h3>Thank you for your feedback!</h3>
            <p>We've received your feedback and will review it soon.</p>
            <p>Your input helps us make DeepDime better!</p>
            <p>We read everything and reply to anything that needs a reply within a couple of days.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-overlay">
      <div className="feedback-modal">
        <div className="feedback-header">
          <h3>Help us improve DeepDime</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="feedback-form">
          {error && (
            <div className="error-message" role="alert">{error}</div>
          )}

          <div className="form-group">
            <label>What is it about</label>
            <select 
              value={feedback.type} 
              onChange={(e) => setFeedback({...feedback, type: e.target.value})}
              className="feedback-select"
            >
              <option value="suggestion">A suggestion</option>
              <option value="bug">Something is broken</option>
              <option value="feature">A feature I want</option>
              <option value="general">General feedback</option>
              <option value="complaint">A complaint</option>
            </select>
          </div>

          <div className="form-group">
            <label>Overall</label>
            <div className="rating-container">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`star ${feedback.rating >= star ? 'filled' : ''}`}
                  onClick={() => setFeedback({...feedback, rating: star})}
                >
                  ★
                </button>
              ))}
              <span className="rating-text">{feedback.rating} of 5</span>
            </div>
          </div>

          <div className="form-group">
            <label>Tell us more</label>
            <textarea
              value={feedback.message}
              onChange={(e) => setFeedback({...feedback, message: e.target.value})}
              placeholder="What happened, or what would make this more useful?"
              rows={4}
              required
              className="feedback-textarea"
            />
          </div>

          <div className="form-group">
            <label>Email, if you want a reply</label>
            <input
              type="email"
              value={feedback.email}
              onChange={(e) => setFeedback({...feedback, email: e.target.value})}
              placeholder={user?.email || "your.email@example.com"}
              className="feedback-input"
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting || !feedback.message.trim()} className="submit-btn">
              {isSubmitting ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>

        <div className="feedback-footer">
          Stored privately; only the DeepDime team reads it.
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;